import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutGrid, DoorOpen, Grid3x3, Ruler, DollarSign, ShoppingCart, LogOut, LayoutDashboard, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import WindowBuilder from '@/components/WindowBuilder';
import AppHeader from '@/components/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Configurator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [width, setWidth] = useState(3);
  const [height, setHeight] = useState(4);
  const [quantity, setQuantity] = useState(1);
  const [category, setCategory] = useState('window');
  const [loading, setLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [category]);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products?category=${category}`, { withCredentials: true });
      setProducts(data);
      if (data.length > 0) setSelectedProduct(data[0]);
      else setSelectedProduct(null);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const calculatePrice = () => {
    if (!selectedProduct) return 0;
    const area = width * height;
    return (selectedProduct.base_price + area * selectedProduct.price_per_sqft) * quantity;
  };

  const searchContacts = async (q) => {
    setContactSearch(q);
    if (q.length < 2) { setContactResults([]); setShowContactDropdown(false); return; }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contacts/search/autocomplete?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setContactResults(data);
      setShowContactDropdown(data.length > 0);
    } catch (e) { setContactResults([]); }
  };

  const selectContact = (contact) => {
    setSelectedContact(contact);
    setContactSearch(contact.name);
    setShowContactDropdown(false);
  };

  const handleCreateQuote = async () => {
    if (!selectedProduct) { toast.error('Please select a product'); return; }
    const customerName = selectedContact?.name || user.name;
    const customerEmail = selectedContact?.email || user.email;
    const customerPhone = selectedContact?.phone || user.phone || '';
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/quotes`, {
        product_id: selectedProduct.id, width, height, quantity,
        customer_name: customerName, customer_email: customerEmail, customer_phone: customerPhone
      }, { withCredentials: true });
      toast.success('Quote created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to create quote');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader title="Hi Tech Industries">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="my-orders-button">
          <ShoppingCart size={18} className="mr-2" /> My Orders
        </Button>
        {user?.role === 'admin' && (
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-slate-600 hover:text-slate-900" data-testid="admin-dashboard-button">
            <LayoutDashboard size={18} className="mr-2" /> Admin
          </Button>
        )}
      </AppHeader>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-2">Design Your Perfect Window</h2>
          <p className="text-sm sm:text-base md:text-lg text-slate-500">Configure dimensions and get instant pricing</p>
        </div>

        {/* Mobile: preview first, then controls. Desktop: sidebar left, preview right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Controls - on mobile these go below the preview */}
          <div className="lg:col-span-4 space-y-4 sm:space-y-6 order-2 lg:order-1">
            {/* Category selector */}
            <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="category-selector">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl font-medium">Product Category</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <Tabs value={category} onValueChange={setCategory} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 bg-slate-100 rounded-sm h-10 sm:h-11">
                    <TabsTrigger value="window" className="data-[state=active]:bg-white rounded-sm text-[11px] sm:text-xs" data-testid="category-window">
                      <LayoutGrid size={14} className="mr-1" /> Windows
                    </TabsTrigger>
                    <TabsTrigger value="door" className="data-[state=active]:bg-white rounded-sm text-[11px] sm:text-xs" data-testid="category-door">
                      <DoorOpen size={14} className="mr-1" /> Doors
                    </TabsTrigger>
                    <TabsTrigger value="partition" className="data-[state=active]:bg-white rounded-sm text-[11px] sm:text-xs" data-testid="category-partition">
                      <Grid3x3 size={14} className="mr-1" /> Partitions
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>

            {/* Product Style */}
            <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="product-selector">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl font-medium">Product Style</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0 space-y-3">
                {products.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No products in this category</p>}
                {products.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => setSelectedProduct(product)}
                    className={`p-3 sm:p-4 border rounded-sm cursor-pointer transition-all duration-150 active:scale-[0.98] ${
                      selectedProduct?.id === product.id ? 'border-[#0052FF] bg-[#EFF6FF]' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`product-option-${product.id}`}
                  >
                    <h4 className="font-medium text-slate-900 text-sm sm:text-base">{product.name}</h4>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1">{product.style} &mdash; {product.material}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                      <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-slate-500">Base</span>
                      <span className="text-sm font-medium text-slate-900 font-mono">&#8377;{product.base_price}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Dimensions */}
            <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="dimension-inputs">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl font-medium flex items-center gap-2">
                  <Ruler size={20} className="text-slate-600" /> Dimensions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Width (ft)</Label>
                    <Input type="number" value={width} onChange={(e) => setWidth(parseFloat(e.target.value) || 0)} min="1" step="0.5"
                      className="mt-1.5 h-10 sm:h-11 rounded-sm border-slate-200 text-center font-mono" data-testid="width-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Height (ft)</Label>
                    <Input type="number" value={height} onChange={(e) => setHeight(parseFloat(e.target.value) || 0)} min="1" step="0.5"
                      className="mt-1.5 h-10 sm:h-11 rounded-sm border-slate-200 text-center font-mono" data-testid="height-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Qty</Label>
                    <Input type="number" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} min="1"
                      className="mt-1.5 h-10 sm:h-11 rounded-sm border-slate-200 text-center font-mono" data-testid="quantity-input" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview + Price - on mobile this goes first */}
          <div className="lg:col-span-8 space-y-4 sm:space-y-6 order-1 lg:order-2">
            <Card className="border border-slate-200 rounded-sm shadow-none bg-[#F1F5F9]" data-testid="preview-area">
              <CardContent className="p-3 sm:p-4">
                {selectedProduct ? (
                  <WindowBuilder
                    category={category}
                    style={selectedProduct.style}
                    material={selectedProduct.material}
                    width={width}
                    height={height}
                    onResize={({ width: w, height: h }) => {
                      if (w !== undefined) setWidth(w);
                      if (h !== undefined) setHeight(h);
                    }}
                  />
                ) : (
                  <div className="h-52 sm:h-80 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <LayoutGrid size={40} className="mx-auto mb-3 text-slate-300" />
                      <p className="text-sm">Select a product to preview</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Price Summary - sticky on mobile */}
            <Card className="border-2 border-[#0052FF] rounded-sm shadow-none bg-[#EFF6FF]/30 lg:sticky lg:top-20" data-testid="price-summary">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign size={24} className="text-[#0052FF]" />
                  <h3 className="text-xl sm:text-2xl font-medium">Price Summary</h3>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                  <div className="flex justify-between text-slate-600"><span>Area:</span><span className="font-mono">{(width * height).toFixed(1)} sqft</span></div>
                  <div className="flex justify-between text-slate-600"><span>Qty:</span><span className="font-mono">{quantity}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Base:</span><span className="font-mono">&#8377;{selectedProduct?.base_price || 0}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Rate:</span><span className="font-mono">&#8377;{selectedProduct?.price_per_sqft || 0}/sqft</span></div>
                </div>
                <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                  <span className="text-lg sm:text-2xl font-medium text-slate-900">Total:</span>
                  <span className="text-2xl sm:text-4xl font-medium text-[#0052FF] font-mono" data-testid="total-price">&#8377;{calculatePrice().toFixed(2)}</span>
                </div>

                {/* Contact auto-fill (admin only) */}
                {user?.role === 'admin' && (
                  <div className="border-t border-slate-200 pt-3 mt-3 relative">
                    <Label className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.15em] text-slate-700">Customer (from contacts)</Label>
                    <div className="relative mt-1.5">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={contactSearch}
                        onChange={(e) => searchContacts(e.target.value)}
                        onFocus={() => contactResults.length > 0 && setShowContactDropdown(true)}
                        placeholder="Search contact by name, phone..."
                        className="pl-8 h-9 rounded-sm border-slate-200 text-sm"
                        data-testid="contact-autofill-input"
                      />
                      {selectedContact && (
                        <button onClick={() => { setSelectedContact(null); setContactSearch(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    {showContactDropdown && (
                      <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto" data-testid="contact-dropdown">
                        {contactResults.map(c => (
                          <button key={c.id} onClick={() => selectContact(c)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors"
                            data-testid={`contact-result-${c.id}`}>
                            <p className="text-sm font-medium text-slate-900">{c.name}</p>
                            <p className="text-xs text-slate-500">{[c.phone, c.email, c.company].filter(Boolean).join(' | ')}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedContact && (
                      <div className="mt-2 p-2 bg-slate-50 rounded-sm border border-slate-100 text-xs text-slate-600" data-testid="selected-contact-info">
                        <span className="font-medium text-slate-900">{selectedContact.name}</span>
                        {selectedContact.phone && <span className="ml-2">{selectedContact.phone}</span>}
                        {selectedContact.email && <span className="ml-2">{selectedContact.email}</span>}
                      </div>
                    )}
                  </div>
                )}

                <Button onClick={handleCreateQuote} disabled={loading || !selectedProduct}
                  className="w-full h-11 sm:h-12 bg-[#0052FF] hover:bg-[#0043D1] text-white text-base sm:text-lg rounded-sm mt-4 transition-all duration-150 active:scale-[0.98]"
                  data-testid="get-quote-button">
                  {loading ? 'Creating Quote...' : 'Get Quote'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
